import { z } from "zod/v4";

export const SUPPORTED_QUESTIONNAIRE_CURRENCIES = [
  "USD",
  "CAD",
  "NZD",
] as const;
export const QuestionnaireCurrencySchema = z.enum(
  SUPPORTED_QUESTIONNAIRE_CURRENCIES,
);
export type QuestionnaireCurrency = z.infer<
  typeof QuestionnaireCurrencySchema
>;

export const QuestionnaireScalarSchema = z.union([
  z.string(),
  z.number(),
  z.boolean(),
  z.null(),
]);
export type QuestionnaireScalar = z.infer<typeof QuestionnaireScalarSchema>;

export type QuestionnaireCondition =
  | {
      op: "equals";
      field: string;
      value: QuestionnaireScalar;
    }
  | {
      op: "hasValue";
      field: string;
    }
  | {
      op: "greaterThan";
      field: string;
      value: number;
    }
  | {
      op: "in";
      field: string;
      values: QuestionnaireScalar[];
    }
  | {
      op: "and" | "or";
      conditions: QuestionnaireCondition[];
    }
  | {
      op: "not";
      condition: QuestionnaireCondition;
    };

export const QuestionnaireConditionSchema: z.ZodType<QuestionnaireCondition> =
  z.lazy(() =>
    z.discriminatedUnion("op", [
      z.object({
        op: z.literal("equals"),
        field: z.string().min(1),
        value: QuestionnaireScalarSchema,
      }),
      z.object({
        op: z.literal("hasValue"),
        field: z.string().min(1),
      }),
      z.object({
        op: z.literal("greaterThan"),
        field: z.string().min(1),
        value: z.number(),
      }),
      z.object({
        op: z.literal("in"),
        field: z.string().min(1),
        values: QuestionnaireScalarSchema.array().min(1),
      }),
      z.object({
        op: z.enum(["and", "or"]),
        conditions: QuestionnaireConditionSchema.array().min(1),
      }),
      z.object({
        op: z.literal("not"),
        condition: QuestionnaireConditionSchema,
      }),
    ]),
  );

export const MoneyValueSchema = z.object({
  amountMinor: z.number().int().safe().nonnegative(),
  currency: QuestionnaireCurrencySchema,
});
export type MoneyValue = z.infer<typeof MoneyValueSchema>;

export const DurationValueSchema = z.object({
  amount: z.number().int().nonnegative().max(600),
  unit: z.enum(["years", "months"]),
});
export type DurationValue = z.infer<typeof DurationValueSchema>;

export const QuestionnaireFileValueSchema = z.object({
  id: z.string().min(1),
  name: z.string().min(1),
  mimeType: z.string().min(1).optional(),
  size: z.number().int().nonnegative().optional(),
});
export type QuestionnaireFileValue = z.infer<
  typeof QuestionnaireFileValueSchema
>;

const resolvedBusinessTypeSchema = z.object({
  id: z.union([z.string().min(1), z.number().int().positive()]),
  name: z.string().min(1),
});

/** A catalog pick, or the words they typed when nothing in the list matched. */
export const QuestionnaireBusinessTypeValueSchema = z.union([
  resolvedBusinessTypeSchema,
  z.string().trim().min(2),
]);
export type QuestionnaireBusinessTypeValue = z.infer<
  typeof QuestionnaireBusinessTypeValueSchema
>;

export const QuestionnaireIndustryCategoryValueSchema = z.object({
  id: z.number().int().positive(),
  name: z.string().min(1),
});
export type QuestionnaireIndustryCategoryValue = z.infer<
  typeof QuestionnaireIndustryCategoryValueSchema
>;

export const QuestionnaireIndustryCategoriesValueSchema =
  QuestionnaireIndustryCategoryValueSchema.array();
export type QuestionnaireIndustryCategoriesValue = z.infer<
  typeof QuestionnaireIndustryCategoriesValueSchema
>;

export const SensitiveSavedValueSchema = z.object({
  saved: z.literal(true),
  masked: z.string().min(1),
});
export type SensitiveSavedValue = z.infer<
  typeof SensitiveSavedValueSchema
>;

export const QuestionnaireOptionSchema = z.object({
  id: z.string().min(1),
  label: z.string().min(1),
  description: z.string().optional(),
});
export type QuestionnaireOption = z.infer<
  typeof QuestionnaireOptionSchema
>;

export const QuestionnaireSensitivitySchema = z.enum([
  "standard",
  "core",
  "sensitive",
  "participant",
]);
export type QuestionnaireSensitivity = z.infer<
  typeof QuestionnaireSensitivitySchema
>;

export const QuestionnaireSourceSchema = z.object({
  system: z.string().min(1),
  reference: z.string().min(1),
  section: z.string().optional(),
});
export type QuestionnaireSource = z.infer<typeof QuestionnaireSourceSchema>;

type BaseQuestionnaireField = {
  id: string;
  binding: string;
  label: string;
  description?: string | null;
  required: boolean;
  requiredWhen?: QuestionnaireCondition;
  visibleWhen?: QuestionnaireCondition;
  sensitivity: QuestionnaireSensitivity;
  source?: QuestionnaireSource;
  analyticsKey?: string;
};

export type QuestionnaireField =
  | (BaseQuestionnaireField & {
      kind: "text" | "long_text" | "phone" | "date";
      minLength?: number;
      maxLength?: number;
      pattern?: string;
      placeholder?: string;
    })
  | (BaseQuestionnaireField & {
      kind: "enum";
      options: QuestionnaireOption[];
    })
  | (BaseQuestionnaireField & {
      kind: "multi_select";
      options: QuestionnaireOption[];
      minSelections?: number;
      maxSelections?: number;
    })
  | (BaseQuestionnaireField & {
      kind: "duration";
    })
  | (BaseQuestionnaireField & {
      kind: "yes_no";
      yesLabel?: string;
      noLabel?: string;
    })
  | (BaseQuestionnaireField & {
      kind: "money";
      currencies: QuestionnaireCurrency[];
      defaultCurrency: QuestionnaireCurrency;
      currencyBinding?: string;
      minimumMinor?: number;
      maximumMinor?: number;
    })
  | (BaseQuestionnaireField & {
      kind: "business_type";
    })
  | (BaseQuestionnaireField & {
      kind: "industry_categories";
    })
  | (BaseQuestionnaireField & {
      kind: "file";
      acceptedMimeTypes?: string[];
      maxFiles?: number;
      maxBytes?: number;
    })
  | (BaseQuestionnaireField & {
      kind: "repeatable_group";
      fields: QuestionnaireField[];
      minItems?: number;
      maxItems?: number;
      addLabel?: string;
    })
  | (BaseQuestionnaireField & {
      kind: "sensitive_identifier";
      identifierType: "ssn" | "tax_id";
      mask?: string;
    })
  | (BaseQuestionnaireField & {
      kind: "consent";
      consentVersion: string;
      body: string;
    })
  | (BaseQuestionnaireField & {
      kind: "computed_display";
      calculationKey: string;
    })
  | (BaseQuestionnaireField & {
      kind: "participant_invite";
      roles: Array<"SPOUSE" | "CO_BUYER">;
    })
  | (BaseQuestionnaireField & {
      kind: "page";
      bullets: string[];
      showTrust: boolean;
      placement: "start" | "end" | "inline";
    });

const baseFieldSchema = z.object({
  id: z.string().min(1),
  binding: z.string().min(1),
  label: z.string().min(1),
  description: z.string().optional().nullable(),
  required: z.boolean().default(false),
  requiredWhen: QuestionnaireConditionSchema.optional(),
  visibleWhen: QuestionnaireConditionSchema.optional(),
  sensitivity: QuestionnaireSensitivitySchema.default("standard"),
  source: QuestionnaireSourceSchema.optional(),
  analyticsKey: z.string().min(1).optional(),
});

const textFieldSchema = (kind: "text" | "long_text" | "phone" | "date") =>
  baseFieldSchema.extend({
    kind: z.literal(kind),
    minLength: z.number().int().nonnegative().optional(),
    maxLength: z.number().int().positive().optional(),
    pattern: z.string().optional(),
    placeholder: z.string().optional(),
  });

export const QuestionnaireFieldSchema: z.ZodType<QuestionnaireField> = z.lazy(
  () =>
    z.discriminatedUnion("kind", [
      textFieldSchema("text"),
      textFieldSchema("long_text"),
      textFieldSchema("phone"),
      textFieldSchema("date"),
      baseFieldSchema.extend({
        kind: z.literal("enum"),
        options: QuestionnaireOptionSchema.array().min(1),
      }),
      baseFieldSchema.extend({
        kind: z.literal("multi_select"),
        options: QuestionnaireOptionSchema.array().min(1),
        minSelections: z.number().int().nonnegative().optional(),
        maxSelections: z.number().int().positive().optional(),
      }),
      baseFieldSchema.extend({
        kind: z.literal("duration"),
      }),
      baseFieldSchema.extend({
        kind: z.literal("yes_no"),
        yesLabel: z.string().optional(),
        noLabel: z.string().optional(),
      }),
      baseFieldSchema.extend({
        kind: z.literal("money"),
        currencies: QuestionnaireCurrencySchema.array().min(1),
        defaultCurrency: QuestionnaireCurrencySchema,
        currencyBinding: z.string().min(1).optional(),
        minimumMinor: z.number().int().safe().nonnegative().optional(),
        maximumMinor: z.number().int().safe().nonnegative().optional(),
      }),
      baseFieldSchema.extend({
        kind: z.literal("business_type"),
      }),
      baseFieldSchema.extend({
        kind: z.literal("industry_categories"),
      }),
      baseFieldSchema.extend({
        kind: z.literal("file"),
        acceptedMimeTypes: z.string().array().optional(),
        maxFiles: z.number().int().positive().optional(),
        maxBytes: z.number().int().positive().optional(),
      }),
      baseFieldSchema.extend({
        kind: z.literal("repeatable_group"),
        fields: QuestionnaireFieldSchema.array().min(1),
        minItems: z.number().int().nonnegative().optional(),
        maxItems: z.number().int().positive().optional(),
        addLabel: z.string().optional(),
      }),
      baseFieldSchema.extend({
        kind: z.literal("sensitive_identifier"),
        identifierType: z.enum(["ssn", "tax_id"]),
        mask: z.string().optional(),
      }),
      baseFieldSchema.extend({
        kind: z.literal("consent"),
        consentVersion: z.string().min(1),
        body: z.string().min(1),
      }),
      baseFieldSchema.extend({
        kind: z.literal("computed_display"),
        calculationKey: z.string().min(1),
      }),
      baseFieldSchema.extend({
        kind: z.literal("participant_invite"),
        roles: z.enum(["SPOUSE", "CO_BUYER"]).array().min(1),
      }),
      baseFieldSchema.extend({
        kind: z.literal("page"),
        bullets: z.string().array().max(3).default([]),
        showTrust: z.boolean().default(false),
        placement: z.enum(["start", "end", "inline"]).default("inline"),
      }),
    ]),
);

export const QuestionnaireTransitionSchema = z.object({
  to: z.string().min(1),
  when: QuestionnaireConditionSchema.optional(),
});
export type QuestionnaireTransition = z.infer<
  typeof QuestionnaireTransitionSchema
>;

export const QuestionnaireStepSchema = z.object({
  id: z.string().min(1),
  title: z.string().min(1),
  description: z.string().optional().nullable(),
  stage: z.string().optional(),
  visibleWhen: QuestionnaireConditionSchema.optional(),
  fields: QuestionnaireFieldSchema.array().min(1),
  transitions: QuestionnaireTransitionSchema.array().optional(),
});
export type QuestionnaireStep = z.infer<typeof QuestionnaireStepSchema>;

export const QuestionnaireDefinitionSchema = z.object({
  id: z.string().min(1),
  version: z.string().min(1),
  title: z.string().min(1),
  description: z.string().optional().nullable(),
  initialStepId: z.string().min(1),
  steps: QuestionnaireStepSchema.array().min(1),
  metadata: z.record(z.string(), QuestionnaireScalarSchema).optional(),
});
export type QuestionnaireDefinition = z.infer<
  typeof QuestionnaireDefinitionSchema
>;

export type QuestionnaireAnswers = Record<string, unknown>;
